import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@mui/material';

import { Icon, InfoIconTooltip } from '@components';
import CurrencyAmount from '@components/currency-amount/CurrencyAmount';
import { DEFAULT_PLACEHOLDER } from '@constants/generic';
import { TRANSACTIONS } from '@constants/routes';
import { DashboardCardType } from '@features/dashboard/helper';
import { transactionsTabs } from '@features/transactions/constants';
import { DEFAULT_CURRENCY_CONFIG } from '@hooks/useCurrency';
import { updateTransactionFilter } from '@layouts/main-header/branchSlice';

interface DashboardCardProps {
  id: DashboardCardType | string;
  title: string;
  value: number;
  icon: string;
  growthValue?: number;
  className?: string;
  tooltipValue?: string;
  isLoading?: boolean;
  showGrowthValue: boolean;
  isClickable?: boolean;
  bgGradient?: string;
}

const DashboardCard = ({
  id,
  title,
  value,
  icon,
  growthValue,
  className,
  tooltipValue,
  isLoading,
  showGrowthValue,
  isClickable = true,
  bgGradient
}: DashboardCardProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currencyConfig } = useSelector((state: any) => state.user);

  const isDefaultCurrency =
    currencyConfig?.code === DEFAULT_CURRENCY_CONFIG.code;

  const iconName = growthValue && growthValue < 0 ? 'decreasing' : 'increasing';

  const handleClick = () => {
    if (!isClickable) return;
    const filter =
      id === DashboardCardType.COLLECTED ||
      id === DashboardCardType.TO_BE_SETTLED
        ? transactionsTabs[1]
        : transactionsTabs[2];

    dispatch(updateTransactionFilter({ filter }));
    navigate(TRANSACTIONS.TRANSACTIONS);
  };

  const changeInPercentage = () =>
    isLoading ? (
      <Skeleton
        height={32}
        width={80}
        key={Math.random()}
        animation="wave"
        variant="text"
      />
    ) : (
      <div
        className={`flex w-fit items-center rounded-xl px-2 text-sm ${
          growthValue && growthValue < 0
            ? 'bg-errorLite text-error'
            : 'bg-primaryLite9 text-primary90'
        }`}
      >
        <Icon
          name={iconName}
          size="medium"
          className="mr-1 !w-3 min-w-4"
          key={iconName}
        />
        <span>{growthValue && Math.abs(growthValue)}%</span>
      </div>
    );

  return (
    <div
      className={`relative aspect-[11/5] max-h-56 min-h-36 rounded-2xl border border-cofeeNeutral4 bg-white p-6 ${className} ${
        !isClickable ? '' : 'cursor-pointer'
      } ${bgGradient}`}
      onClick={handleClick}
      role="presentation"
    >
      {icon && (
        <div className="absolute right-6 top-6 flex size-12 items-center justify-center rounded-full border border-cofeeNeutral4 bg-white">
          <Icon name={icon} size="24" />
        </div>
      )}

      <div className="mb-2 flex items-center gap-3 2xl:mb-4">
        <p className="text-base font-medium text-cofeeNeutral8 xl:text-lg">
          {title}
        </p>
        <div>
          {tooltipValue && (
            <InfoIconTooltip
              id={title.replace(/\s+/g, '-')}
              tooltipText={tooltipValue}
              classname="max-w-400"
            />
          )}
        </div>
      </div>
      <div className="flex flex-col gap-1 xl:gap-1.5 2xl:gap-4">
        <div
          className={`flex items-center text-3xl leading-8 text-cofeeNeutral10 lg:text-xl xl:text-4xl 3xl:text-40 ${
            id === DashboardCardType.COLLECTED ? '' : 'pb-6 2xl:pb-9'
          }`}
        >
          {isLoading ? (
            <Skeleton
              height={32}
              width={80}
              key={Math.random()}
              animation="wave"
              variant="text"
            />
          ) : (
            <CurrencyAmount
              value={value}
              className=""
              symbolClass={
                isDefaultCurrency
                  ? ''
                  : '!text-lg lg:!text-xl xl:!text-2xl 3xl:!text-3xl !font-medium'
              }
              valueClass="pl-2 font-semibold"
            />
          )}
        </div>
        {showGrowthValue
          ? changeInPercentage()
          : id === DashboardCardType.COLLECTED && (
              <div className="flex w-fit items-center rounded-xl bg-cofeeNeutral3 px-2 text-sm text-cofeeNeutral7">
                <span>{DEFAULT_PLACEHOLDER}</span>
              </div>
            )}
      </div>
    </div>
  );
};

export default DashboardCard;
